import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Input, RadioButton, TextArea } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function ALTFCLivingSkills(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const yesNoOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <p>
                The assigned SAFY worker uses this form to interview the client
                to determine the client’s readiness for living independently. It
                is completed within the first thirty days of admission to a SAFY
                program (if the client is 16 years of age or older) or 30 days
                prior to a client’s 16<sup>th</sup> birthday (this age may vary
                per state so check your state rules regarding the age to begin
                assessing independent living skills). This tool is then utilized
                along with the Part I and Part II Inventories in developing
                goals for the independent living section of the treatment plan.
              </p>
              <p>
                NOTE: If the referring agency has completed an assessment of IL
                skills utilizing another tool, this form may be deleted. But a
                copy of the other assessment tool must be placed in the client
                file.
              </p>
              <div className="container">
                <div className="row">
                  <div className="col-3">
                    <Input
                      name="DateCompleted"
                      label="Date Completed"
                      type="date"
                      error={errors.DateCompleted}
                      value={data.DateCompleted["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <RadioButton
                name="LivedAlone"
                label="1. Has the youth ever lived on his/her own before?"
                options={yesNoOptions}
                error={errors.LivedAlone}
                value={data.LivedAlone["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.LivedAlone["@value"] === "1" && (
                <TextArea
                  name="LivedAloneDetails"
                  label="If yes, at what age and for how long?"
                  error={errors.LivedAloneDetails}
                  value={data.LivedAloneDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("LivedAloneDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <TextArea
                name="Supports"
                label="2. Who does the youth believe will be his/her support person(s) in getting out there on their own?"
                error={errors.Supports}
                value={data.Supports["@value"]}
                onChange={handleChange}
                identical={compare("Supports")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="LiveLocation"
                label="3. Where does the youth want to live and why? When do they think this will happen?"
                error={errors.LiveLocation}
                value={data.LiveLocation["@value"]}
                onChange={handleChange}
                identical={compare("LiveLocation")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="ImportantSkills"
                label="4. What IL skills does the youth identify as most important to live on their own? Does he/she feel they have those skills?"
                error={errors.ImportantSkills}
                value={data.ImportantSkills["@value"]}
                onChange={handleChange}
                identical={compare("ImportantSkills")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="Bills"
                label="5. Does the youth have a basic knowledge on what bills he/she will need to pay to live on their own?"
                options={yesNoOptions}
                error={errors.Bills}
                value={data.Bills["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Bills["@value"] === "0" && (
                <TextArea
                  name="BillsAddressed"
                  label="If not, what areas need to be addressed in the skill development?"
                  error={errors.BillsAddressed}
                  value={data.BillsAddressed["@value"]}
                  onChange={handleChange}
                  identical={compare("BillsAddressed")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <TextArea
                name="SecurityDeposit"
                label="6. Does the youth understand what a security deposit is and how to get it back?"
                error={errors.SecurityDeposit}
                value={data.SecurityDeposit["@value"]}
                onChange={handleChange}
                identical={compare("SecurityDeposit")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Lease"
                label="7. Has the youth ever signed or seen a lease?"
                error={errors.Lease}
                value={data.Lease["@value"]}
                onChange={handleChange}
                identical={compare("Lease")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Utilities"
                label="8. Can the youth verbally tell you how to get utilities and the phone turned on in their name?"
                error={errors.Utilities}
                value={data.Utilities["@value"]}
                onChange={handleChange}
                identical={compare("Utilities")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="CostOfLiving"
                label="9. Can the youth describe to you a realistic cost of living in respects to a one bedroom apartment?"
                error={errors.CostOfLiving}
                value={data.CostOfLiving["@value"]}
                onChange={handleChange}
                identical={compare("CostOfLiving")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="SetUp"
                label="10. Does the youth know what is needed to set up an apartment? Where and how to get these things?"
                error={errors.SetUp}
                value={data.SetUp["@value"]}
                onChange={handleChange}
                identical={compare("SetUp")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="HouseRules"
                label="11. What house rules would the youth see he/she would need in their apartment?"
                error={errors.HouseRules}
                value={data.HouseRules["@value"]}
                onChange={handleChange}
                identical={compare("HouseRules")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Evicted"
                label="12. Do they understand what would cause them to get evicted from a rental?"
                error={errors.Evicted}
                value={data.Evicted["@value"]}
                onChange={handleChange}
                identical={compare("Evicted")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="UnfairLandlord"
                label="13. What would the youth do if they thought their landlord was treating them unfairly?"
                error={errors.UnfairLandlord}
                value={data.UnfairLandlord["@value"]}
                onChange={handleChange}
                identical={compare("UnfairLandlord")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Budget"
                label="14. Does the youth know how to do a budget? Do they understand the purpose of the budget?"
                error={errors.Budget}
                value={data.Budget["@value"]}
                onChange={handleChange}
                identical={compare("Budget")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Transportation"
                label="15. What forms of transportation would the youth identify as being available? Can they explain to you how to access them?"
                error={errors.Transportation}
                value={data.Transportation["@value"]}
                onChange={handleChange}
                identical={compare("Transportation")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="Job"
                label="16. Has the youth had a job before?"
                options={yesNoOptions}
                error={errors.Job}
                value={data.Job["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Job["@value"] === "1" && (
                <TextArea
                  name="JobDetails"
                  label="If yes, where, what position, how many hours per week, and for how long?"
                  error={errors.JobDetails}
                  value={data.JobDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("JobDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}

              <TextArea
                name="FindJob"
                label="17. Can the youth verbalize various methods on how to find a job?"
                error={errors.FindJob}
                value={data.FindJob["@value"]}
                onChange={handleChange}
                identical={compare("FindJob")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="LoseJob"
                label="18. Can the youth tell you four things they could do that would cause you to lose your job?"
                error={errors.LoseJob}
                value={data.LoseJob["@value"]}
                onChange={handleChange}
                identical={compare("LoseJob")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Paycheck"
                label="19. Can the youth identify what and why things are taken out of a paycheck?"
                error={errors.Paycheck}
                value={data.Paycheck["@value"]}
                onChange={handleChange}
                identical={compare("Paycheck")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Taxes"
                label="20. Has the youth ever filed Federal/State income taxes?"
                error={errors.Taxes}
                value={data.Taxes["@value"]}
                onChange={handleChange}
                identical={compare("Taxes")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Healthy"
                label="21. Can the youth describe to you clearly what it takes to stay healthy?"
                error={errors.Healthy}
                value={data.Healthy["@value"]}
                onChange={handleChange}
                identical={compare("Healthy")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Cook"
                label="22. List five things that the youth can cook for themselves:"
                error={errors.Cook}
                value={data.Cook["@value"]}
                onChange={handleChange}
                identical={compare("Cook")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="SelfCare"
                label="23. Does the youth understand self-care for things like colds, minor cuts, etc.?"
                error={errors.SelfCare}
                value={data.SelfCare["@value"]}
                onChange={handleChange}
                identical={compare("SelfCare")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="HealthProvider"
                label="24. Does the youth know when and how to contact a health provider? Do they understand the cost?"
                error={errors.HealthProvider}
                value={data.HealthProvider["@value"]}
                onChange={handleChange}
                identical={compare("HealthProvider")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Insurance"
                label="25. Does the youth understand Medical Insurance/Medicaid coverage and how to get such?"
                error={errors.Insurance}
                value={data.Insurance["@value"]}
                onChange={handleChange}
                identical={compare("Insurance")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="BirthControl"
                label="26. Does the youth have a sound understanding of birth control?"
                error={errors.BirthControl}
                value={data.BirthControl["@value"]}
                onChange={handleChange}
                identical={compare("BirthControl")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="SignsOfSTD"
                label="27. Does the youth understand what the signs of sexually transmitted diseases are?"
                error={errors.SignsOfSTD}
                value={data.SignsOfSTD["@value"]}
                onChange={handleChange}
                identical={compare("SignsOfSTD")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="SocialSecurity"
                label="28. Does the youth know their SS# and its importance? (Ask to see their card)"
                error={errors.SocialSecurity}
                value={data.SocialSecurity["@value"]}
                onChange={handleChange}
                identical={compare("SocialSecurity")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="EmergencyPersonnel"
                label="29. Can the youth tell you how to access emergency personnel? (police, fire dept., EMS, etc)"
                error={errors.EmergencyPersonnel}
                value={data.EmergencyPersonnel["@value"]}
                onChange={handleChange}
                identical={compare("EmergencyPersonnel")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="NewToArea"
                label="30. If the youth was new to an area, would he/she be able to locate needed places such as barber/salon, Laundromat, dry cleaners, or other basic service providers?"
                error={errors.NewToArea}
                value={data.NewToArea["@value"]}
                onChange={handleChange}
                identical={compare("NewToArea")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Chores"
                label="31. What chores would the youth see as chores that need to be done daily if living on own?"
                error={errors.Chores}
                value={data.Chores["@value"]}
                onChange={handleChange}
                identical={compare("Chores")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="FreeTime"
                label="32. How does the youth spend their free time?"
                error={errors.FreeTime}
                value={data.FreeTime["@value"]}
                onChange={handleChange}
                identical={compare("FreeTime")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="HardestPart"
                label="33. What does the youth believe will be the hardest part about living on their own?"
                error={errors.HardestPart}
                value={data.HardestPart["@value"]}
                onChange={handleChange}
                identical={compare("HardestPart")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Accomplish"
                label="34. What are four things the youth would like to accomplish during the next two years?"
                error={errors.Accomplish}
                value={data.Accomplish["@value"]}
                onChange={handleChange}
                identical={compare("Accomplish")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="LifeSkills"
                label="35. What three Life Skills do they feel need improvement and why?"
                error={errors.LifeSkills}
                value={data.LifeSkills["@value"]}
                onChange={handleChange}
                identical={compare("LifeSkills")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <span>36. How does the youth complete these sentences?</span>
              <TextArea
                name="Happiest"
                label="I’m happiest when I..."
                error={errors.Happiest}
                value={data.Happiest["@value"]}
                onChange={handleChange}
                identical={compare("Happiest")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="BestThing"
                label="The best thing about me is..."
                error={errors.BestThing}
                value={data.BestThing["@value"]}
                onChange={handleChange}
                identical={compare("BestThing")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Question"
                label="One question I have related to being on my own is..."
                error={errors.Question}
                value={data.Question["@value"]}
                onChange={handleChange}
                identical={compare("Question")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="DoWell"
                label="Something I do well is..."
                error={errors.DoWell}
                value={data.DoWell["@value"]}
                onChange={handleChange}
                identical={compare("DoWell")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default ALTFCLivingSkills;
